import React, { useMemo } from 'react';
import { formatShortDateTime } from '../../utils/timeUtils';

const STATUS_LABELS = {
  active: 'Tillgänglig',
  rate_limited: 'Rate limit',
  no_credits: 'Inga credits',
  auth_error: 'Auth-fel',
  not_configured: 'Inte konfigurerad',
  disabled: 'Avstängd',
  error: 'Fel'
};

const STATUS_STYLES = {
  active: 'bg-emerald-500/20 text-emerald-200 border border-emerald-500/30',
  rate_limited: 'bg-amber-500/20 text-amber-200 border border-amber-500/40',
  no_credits: 'bg-amber-500/20 text-amber-200 border border-amber-500/40',
  auth_error: 'bg-red-500/20 text-red-200 border border-red-500/40',
  not_configured: 'bg-slate-700/60 text-slate-200 border border-slate-600',
  disabled: 'bg-slate-700/60 text-slate-200 border border-slate-600',
  error: 'bg-red-500/20 text-red-200 border border-red-500/40'
};

const formatRetryInfo = (provider) => {
  if (!provider) return null;
  if (provider.retryAt) {
    const retryDate = new Date(provider.retryAt);
    if (!Number.isNaN(retryDate.getTime())) {
      const diffMs = retryDate.getTime() - Date.now();
      if (diffMs > 0) {
        const minutes = Math.max(1, Math.ceil(diffMs / 60000));
        return `Försök igen ${formatShortDateTime(retryDate)} (om ${minutes} min)`;
      }
      return `Försök igen ${formatShortDateTime(retryDate)}`;
    }
  }
  if (provider.retryAfterSeconds) {
    const seconds = Math.max(0, Number(provider.retryAfterSeconds));
    const minutes = Math.ceil(seconds / 60);
    return `Försök igen om ${minutes} min`;
  }
  return null;
};

const formatQuota = (provider) => {
  const requests = Number.isFinite(provider?.remainingRequests) ? provider.remainingRequests : null;
  const tokens = Number.isFinite(provider?.remainingTokens) ? provider.remainingTokens : null;
  if (requests == null && tokens == null) return null;
  const parts = [];
  if (requests != null) parts.push(`Anrop kvar: ${requests}`);
  if (tokens != null) parts.push(`Tokens kvar: ${tokens}`);
  return parts.join(' · ');
};

const getStatusHint = (provider, statusKey, retryInfo) => {
  if (statusKey === 'active') return null;
  if (statusKey === 'rate_limited') {
    return retryInfo ? `Rate limit nådd. ${retryInfo}.` : 'Rate limit nådd. Försök igen senare.';
  }
  if (statusKey === 'no_credits') {
    return 'Inga credits kvar. Lägg till betalning eller fyll på.';
  }
  if (statusKey === 'auth_error') {
    return 'Kontrollera API‑nyckeln och aktivera provider.';
  }
  if (statusKey === 'not_configured') {
    return 'Lägg in API‑nyckel för att aktivera providern.';
  }
  if (statusKey === 'disabled') {
    return 'Providern är avstängd i inställningarna.';
  }
  if (provider?.error) {
    return 'Kontrollera feltexten och testa igen.';
  }
  return null;
};

const ProviderStatusPanel = ({
  status,
  loading = false,
  error = null,
  onRefresh,
  title = 'AI-providerstatus',
  compact = false
}) => {
  const sortedProviders = useMemo(() => {
    const providers = status?.providers || [];
    return [...providers].sort((a, b) => {
      const nameA = a.label || a.name || '';
      const nameB = b.label || b.name || '';
      return nameA.localeCompare(nameB, 'sv');
    });
  }, [status]);

  return (
    <div className="rounded-xl border border-slate-800 bg-slate-900/60 p-5 space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="text-base font-semibold text-white">{title}</h3>
          {status?.checkedAt && (
            <p className="text-xs text-slate-500">
              Senast testad: {formatShortDateTime(new Date(status.checkedAt))}
            </p>
          )}
        </div>
        {onRefresh && (
          <button
            onClick={onRefresh}
            disabled={loading}
            className="px-3 py-2 rounded-lg bg-slate-800 text-slate-200 text-xs hover:bg-slate-700 disabled:opacity-60"
          >
            {loading ? 'Testar...' : 'Testa igen'}
          </button>
        )}
      </div>

      {error && (
        <div className="rounded-lg border border-red-500/40 bg-red-500/10 p-3 text-sm text-red-200">
          {error}
        </div>
      )}

      {loading && !status && (
        <div className="flex items-center gap-2 text-sm text-slate-400">
          <div className="h-4 w-4 rounded-full border-2 border-cyan-400 border-t-transparent animate-spin" />
          Hämtar status...
        </div>
      )}

      {status && (
        <>
          <div className="text-xs text-slate-400">
            {status.summary?.active || 0} av {status.summary?.total || 0} providers är tillgängliga
          </div>
          <div className={`grid gap-3 ${compact ? 'md:grid-cols-1' : 'md:grid-cols-2'}`}>
            {sortedProviders.map((provider) => {
              const statusKey = provider.status || (provider.available ? 'active' : 'error');
              const badgeClass = STATUS_STYLES[statusKey] || STATUS_STYLES.error;
              const label = provider.label || provider.name;
              const quota = formatQuota(provider);
              const retryInfo = formatRetryInfo(provider);
              const hint = getStatusHint(provider, statusKey, retryInfo);
              return (
                <div key={provider.name} className={`rounded-lg p-3 ${badgeClass}`}>
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-semibold">{label}</span>
                    <span className="text-xs uppercase tracking-wide">
                      {STATUS_LABELS[statusKey] || statusKey}
                    </span>
                  </div>
                  {provider.model && (
                    <div className="text-xs text-slate-200/80 mt-1">Model: {provider.model}</div>
                  )}
                  {quota && (
                    <div className="text-xs text-slate-200/80 mt-1">{quota}</div>
                  )}
                  {retryInfo && (
                    <div className="text-xs text-slate-200/80 mt-1">{retryInfo}</div>
                  )}
                  {provider.error && (
                    <div className="text-xs text-slate-100/80 mt-2">{provider.error}</div>
                  )}
                  {hint && (
                    <div className="text-xs text-slate-100/80 mt-2">Åtgärd: {hint}</div>
                  )}
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
};

export default ProviderStatusPanel;
